import type { SweepLocation } from '../types/sweep';

function teamNum(loc: SweepLocation): number | null {
  const t = loc.teamNumber;
  return typeof t === 'number' && Number.isFinite(t) ? Math.round(t) : null;
}

function hasCoords(l: SweepLocation): boolean {
  const la = l.latitude;
  const ln = l.longitude;
  return typeof la === 'number' && typeof ln === 'number' && Number.isFinite(la) && Number.isFinite(ln);
}

function centroidForAssign(
  locations: SweepLocation[],
  assign: Map<string, number | null>,
  team: number | null
): { lat: number; lng: number } | null {
  const pts: SweepLocation[] = [];
  for (const l of locations) {
    const id = String(l.id);
    const t = assign.has(id) ? assign.get(id)! : teamNum(l);
    if (t !== team) continue;
    if (!hasCoords(l)) continue;
    pts.push(l);
  }
  if (!pts.length) return null;
  let slat = 0;
  let slng = 0;
  for (const p of pts) {
    slat += p.latitude as number;
    slng += p.longitude as number;
  }
  return { lat: slat / pts.length, lng: slng / pts.length };
}

function dist2(loc: SweepLocation, c: { lat: number; lng: number }): number {
  const la = loc.latitude as number;
  const ln = loc.longitude as number;
  return (la - c.lat) ** 2 + (ln - c.lng) ** 2;
}

export type TeamMoveProposal = {
  id: string;
  name: string;
  fromTeam: number | null;
  toTeam: number | null;
  accepted: boolean;
};

/**
 * Greedy improvement on movable (not checked-off) stops with coordinates.
 */
export function suggestSweepTeamReassignment(
  locations: SweepLocation[],
  opts?: { maxRounds?: number; maxSpreadFromEven?: number }
): TeamMoveProposal[] {
  const maxRounds = opts?.maxRounds ?? 2;
  const maxSpread = opts?.maxSpreadFromEven ?? 4;

  const assign = new Map<string, number | null>();
  for (const l of locations) {
    assign.set(String(l.id), teamNum(l));
  }

  const movable = locations.filter((l) => !l.sweepCheckedOff && hasCoords(l));
  if (movable.length < 2) return [];

  const teams = Array.from(
    new Set(locations.map((l) => teamNum(l)).filter((t): t is number => t != null))
  ).sort((a, b) => a - b);
  if (teams.length < 2) return [];

  const movableIds = new Set(movable.map((m) => String(m.id)));

  const countMovableOnTeam = (tn: number | null): number => {
    let n = 0;
    for (const l of locations) {
      const id = String(l.id);
      if (!movableIds.has(id)) continue;
      const t = assign.get(id) ?? teamNum(l);
      if (t === tn) n += 1;
    }
    return n;
  };

  const even = Math.ceil(movable.length / teams.length);

  for (let round = 0; round < maxRounds; round++) {
    let changed = false;
    for (const loc of movable) {
      const id = String(loc.id);
      const cur = assign.get(id) ?? teamNum(loc);
      let bestTeam = cur;
      let bestScore = Infinity;
      for (const t of teams) {
        const c = centroidForAssign(locations, assign, t);
        if (!c) continue;
        const d = dist2(loc, c);
        if (d < bestScore) {
          bestScore = d;
          bestTeam = t;
        }
      }
      if (bestTeam === cur) continue;
      if (countMovableOnTeam(cur) <= 1) continue;
      if (countMovableOnTeam(bestTeam) + 1 > even + maxSpread) continue;
      assign.set(id, bestTeam);
      changed = true;
    }
    if (!changed) break;
  }

  const proposals: TeamMoveProposal[] = [];
  for (const loc of movable) {
    const id = String(loc.id);
    const from = teamNum(loc);
    const to = assign.get(id) ?? from;
    if (from === to) continue;
    proposals.push({
      id,
      name: String(loc.name || '').trim() || id.slice(0, 8),
      fromTeam: from,
      toTeam: to,
      accepted: true,
    });
  }
  return proposals;
}

export function teamOverlayFromProposals(proposals: TeamMoveProposal[], acceptedOnly = true): Record<string, number> {
  const out: Record<string, number> = {};
  for (const p of proposals) {
    if (acceptedOnly && !p.accepted) continue;
    if (p.toTeam == null) continue;
    out[p.id] = p.toTeam;
  }
  return out;
}
